import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import EngineIcon from "@/components/engines/EngineIcon";
import { traceableFromConfig } from "@/data/printGeometry";
import { Printer, ArrowLeft, FileText, Loader2, CheckSquare } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E"];

function TraceRow({ cfg }) {
  const t = traceableFromConfig(cfg);
  if (!t) return null;
  const copies = t.width > 300 ? 1 : t.width > 160 ? 2 : 3;
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginTop: 8 }} data-testid="worksheet-trace-row">
      {Array.from({ length: copies }).map((_, i) => (
        <svg key={i} viewBox={t.viewBox} style={{ height: 110, width: (110 * t.width) / 100, border: "1px solid #E5DECF", borderRadius: 8 }}>
          {[t.guidelines.top, t.guidelines.mid, t.guidelines.base].map((y) => <line key={y} x1="0" x2={t.width} y1={y} y2={y} stroke={y === t.guidelines.mid ? "#CBD5E1" : "#94A3B8"} strokeWidth="0.6" strokeDasharray={y === t.guidelines.mid ? "3 3" : ""} />)}
          {t.strokes.map((s, j) => <path key={j} d={s.d} fill="none" stroke={i === 0 ? "#64748B" : "#B8C0CC"} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 3" />)}
          <circle cx={t.startingPoint.x} cy={t.startingPoint.y} r="2.6" fill="#10B981" />
        </svg>
      ))}
    </div>
  );
}

function IconRow({ icon, count }) {
  return (
    <span style={{ display: "inline-flex", gap: 4, verticalAlign: "middle" }}>
      {Array.from({ length: Math.max(0, count) }).map((_, i) => (
        <EngineIcon key={i} name={icon} size={26} strokeWidth={1.8} color="#1F2937" />
      ))}
    </span>
  );
}

function ActivityBlock({ act, index, showKey }) {
  const c = act.config || {};
  return (
    <div className="worksheet-item" style={{ breakInside: "avoid", marginBottom: 26 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontWeight: 800, color: "#0D9488", fontSize: 18 }}>{index + 1}.</span>
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1F2937" }}>{act.title}</span>
      </div>
      {act.prompt && act.prompt !== c.question && <div style={{ marginLeft: 24, marginTop: 2, color: "#4B5563" }}>{act.prompt}</div>}

      <div style={{ marginLeft: 24, marginTop: 10 }}>
        {act.engine === "mcq" && (
          <>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{c.question}</div>
            {(c.options || []).map((o, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0" }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid #94A3B8", display: "inline-block" }} />
                <b style={{ color: "#0D9488" }}>{LETTERS[i]}.</b> <span>{o}</span>
              </div>
            ))}
          </>
        )}

        {act.engine === "count" && (
          <div style={{ fontSize: 18 }}>
            {c.stage === "add" ? (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <IconRow icon={c.icon} count={c.a} /> <b>+</b> <IconRow icon={c.icon} count={c.b} /> <b>=</b>
                <span style={{ borderBottom: "2px solid #1F2937", minWidth: 60, display: "inline-block" }}>&nbsp;</span>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <span>How many?</span> <IconRow icon={c.icon} count={c.a} /> <b>=</b>
                <span style={{ borderBottom: "2px solid #1F2937", minWidth: 60, display: "inline-block" }}>&nbsp;</span>
              </div>
            )}
          </div>
        )}

        {act.engine === "phonics" && (
          <div>
            <div style={{ fontSize: 22, letterSpacing: 4, fontWeight: 700 }}>
              {(c.sounds && c.sounds.length ? c.sounds.map((s) => s.g) : String(c.word || "").split("")).join(" – ")}
            </div>
            <div style={{ marginTop: 10, color: "#4B5563" }}>Write the word:</div>
            <div style={{ borderBottom: "2px solid #1F2937", height: 30, maxWidth: 260 }} />
          </div>
        )}

        {act.engine === "match" && (
          <div style={{ display: "flex", justifyContent: "space-between", maxWidth: 420, gap: 40 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>{c.leftLabel || "Item"}</div>
              {(c.pairs || []).map((p, i) => <div key={i} style={{ margin: "10px 0", fontSize: 16 }}>● {p.left}</div>)}
            </div>
            <div style={{ alignSelf: "center", color: "#94A3B8", fontStyle: "italic" }}>draw a line →</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>{c.rightLabel || "Match"}</div>
              {shuffle(c.pairs || []).map((p, i) => <div key={i} style={{ margin: "10px 0", fontSize: 16 }}>{p.right} ●</div>)}
            </div>
          </div>
        )}

        {act.engine === "trace" && <TraceRow cfg={c} />}

        {act.engine === "sink_float" && (
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr><th style={{ textAlign: "left", padding: "4px 24px 4px 0" }}>Object</th><th style={{ padding: "0 16px" }}>Sink</th><th style={{ padding: "0 16px" }}>Float</th></tr>
            </thead>
            <tbody>
              {(c.items || []).map((it, i) => (
                <tr key={i}>
                  <td style={{ padding: "6px 24px 6px 0", display: "flex", alignItems: "center", gap: 8 }}><EngineIcon name={it.icon} size={22} color="#1F2937" /> {it.name}</td>
                  <td style={{ textAlign: "center" }}><span style={{ width: 20, height: 20, border: "2px solid #94A3B8", display: "inline-block" }} /></td>
                  <td style={{ textAlign: "center" }}><span style={{ width: 20, height: 20, border: "2px solid #94A3B8", display: "inline-block" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showKey && <div className="answer-key-line" style={{ marginLeft: 24, marginTop: 6, fontSize: 13, color: "#059669", fontWeight: 700 }}>Answer: {answerText(act)}</div>}
    </div>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

function answerText(act) {
  const c = act.config || {};
  if (act.engine === "mcq") return `${LETTERS[c.answer] || "?"} (${(c.options || [])[c.answer]})`;
  if (act.engine === "count") return String(c.stage === "add" ? c.answer : c.target);
  if (act.engine === "phonics") return c.mode === "blend" ? String((c.options || [])[c.answer] || c.word) : String(c.word || "");
  if (act.engine === "match") return (c.pairs || []).map((p) => `${p.left}→${p.right}`).join(", ");
  if (act.engine === "sink_float") return (c.items || []).map((it) => `${it.name}: ${it.floats ? "Float" : "Sink"}`).join(", ");
  if (act.engine === "trace") return `Trace: ${String(c.text || "").replace(/_/g, " ")}`;
  return "—";
}

export default function LessonWorksheet() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [showKey, setShowKey] = useState(false);

  useEffect(() => { api.getLesson(lessonId).then(setLesson).catch(() => setLesson(false)); }, [lessonId]);

  if (lesson === false) return <div className="p-16 text-center font-heading text-xl text-[#6B7280]">Lesson not found.</div>;
  if (!lesson) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  const activities = lesson.activities || [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <style>{`@media print { .no-print { display: none !important; } .sheet { border: 0 !important; box-shadow: none !important; padding: 0 !important; } @page { margin: 16mm; } }`}</style>

      <div className="no-print">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#0D9488]"><ArrowLeft size={16} /> Back</button>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Printable Worksheet</div>
            <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{lesson.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Student name (optional)" className="rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" data-testid="ws-name" />
            <button onClick={() => setShowKey((v) => !v)} data-testid="ws-toggle-key" className={`inline-flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 text-sm font-bold ${showKey ? "border-[#10B981] text-[#047857]" : "border-[#E5DECF] text-[#6B7280]"}`}><CheckSquare size={15} /> Answer key</button>
            <button onClick={() => window.print()} data-testid="ws-print" className="inline-flex items-center gap-2 rounded-xl bg-[#0D9488] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74]"><Printer size={16} /> Print</button>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="sheet mt-6 rounded-2xl border border-[#E5DECF] bg-white p-8" data-testid="worksheet-sheet">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #E5DECF", paddingBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 20, color: "#1F2937" }}><FileText size={22} /> {lesson.title}</div>
          <div style={{ fontSize: 14, color: "#6B7280" }}>Name: {studentName || "______________"} &nbsp; Date: __________</div>
        </div>
        {lesson.objective && <div style={{ marginTop: 8, marginBottom: 18, color: "#4B5563", fontStyle: "italic" }}>{lesson.objective}</div>}
        {activities.length === 0 ? (
          <p style={{ textAlign: "center", color: "#94A3B8", marginTop: 24 }}>This lesson has no printable activities.</p>
        ) : activities.map((a, i) => <ActivityBlock key={a.id || i} act={a} index={i} showKey={showKey} />)}
      </div>
    </div>
  );
}
